import { useCallback, useEffect, useRef, useState } from 'react';
import { buildReportGridModel } from './core/model';

// Dados carregados permanecem apenas em memória durante a sessão do dashboard.
export default function useReportDefinitions(items, api, scope) {
  const [definitions, setDefinitions] = useState({});
  const [revision, setRevision] = useState(0);
  const cache = useRef(new Map());

  useEffect(() => () => {
    cache.current.forEach((entry) => entry.controller.abort());
    cache.current.clear();
  }, [scope]);

  useEffect(() => {
    let active = true;
    const next = {};
    const pending = [];
    const liveKeys = new Set();

    items.forEach((item) => {
      const saved = item.meta?.reportGrid;
      if (item.type !== 'ReportGrid' || saved?.version !== 1 || !saved.configuration || !item.style) return;
      const base = { version: 1, configuration: saved.configuration, settings: item.style };
      if (!saved.configuration.saveConfigurationOnly) {
        if (item.data) next[item.id] = { ...base, dataset: item.data };
        return;
      }

      const key = JSON.stringify([scope, item.id, saved.configuration]);
      liveKeys.add(key);
      let entry = cache.current.get(key);
      if (!entry) {
        const controller = new AbortController();
        entry = { controller, dataset: null, error: '', loading: true };
        cache.current.set(key, entry);
        entry.promise = Promise.resolve()
          .then(() => api.genReportGridData(saved.configuration, controller.signal))
          .then((result) => {
            if (controller.signal.aborted) return;
            if (result.error) throw new Error(result.msg || 'Não foi possível carregar o explorador.');
            buildReportGridModel(result.data);
            entry.dataset = result.data;
          })
          .catch((failure) => {
            if (!controller.signal.aborted) entry.error = failure.message || 'Não foi possível carregar o explorador.';
          })
          .finally(() => { entry.loading = false; });
      }
      next[item.id] = { ...base, dataset: entry.dataset, loading: entry.loading, error: entry.error };
      if (entry.loading) pending.push({ id: item.id, entry });
    });

    cache.current.forEach((entry, key) => {
      if (!liveKeys.has(key)) {
        entry.controller.abort();
        cache.current.delete(key);
      }
    });
    setDefinitions(next);
    pending.forEach(({ id, entry }) => {
      entry.promise.then(() => {
        if (!active || entry.controller.signal.aborted) return;
        setDefinitions((previous) => (!previous[id] ? previous : {
          ...previous,
          [id]: { ...previous[id], dataset: entry.dataset, loading: false, error: entry.error },
        }));
      });
    });
    return () => { active = false; };
  }, [items, api, scope, revision]);

  const retry = useCallback((id) => {
    cache.current.forEach((entry, key) => {
      if (JSON.parse(key)[1] === id) {
        entry.controller.abort();
        cache.current.delete(key);
      }
    });
    setRevision((value) => value + 1);
  }, []);

  return [definitions, setDefinitions, retry];
}
